import { Component, inject, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { authBackendEndPointToken } from '../app.config';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ErrorService } from '../shared/error.service';
import { SuperAdminDetailsComponent } from './superadmin-details.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogData } from '../practitioner-list/confirm-action-dialog.component';

export interface SuperAdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber?: string;
  isApproved: boolean;
  createdAt: string;
  lastLoginAt?: string;
  status: 'Active' | 'Deactivated';
}

@Component({
  selector: 'app-superadmin-list',
  standalone: true,
  imports: [
    RouterModule, CommonModule, MatCardModule, MatButtonModule,
    MatIconModule, MatTableModule, MatDividerModule, EmptyStateComponent,
    MatSortModule, MatMenuModule, MatTabsModule, MatProgressSpinnerModule
  ],
  templateUrl: './superadmin-list.component.html',
  styleUrl: './superadmin-list.component.scss'
})
export class SuperadminListComponent implements AfterViewInit {
  currentSegment: 'active' | 'deactivated' = 'active';
  
  immutableTableData: SuperAdminUser[] = [];
  tableDataSource = new MatTableDataSource<SuperAdminUser>();
  tableColumns: string[] = ['name', 'email', 'phoneNumber', 'lastLogin', 'createdDate', 'status', 'action'];
  
  pageTitle = 'Active SuperAdmin Users';
  pageSubtitle = 'Manage system super administrators';
  isLoading = false;

  // Store current sort state
  currentSort: Sort = { active: 'createdDate', direction: 'desc' };

  http = inject(HttpClient);
  router = inject(Router);
  dialog = inject(MatDialog);
  _sn = inject(MatSnackBar);
  backendApi = inject(authBackendEndPointToken);
  errorService = inject(ErrorService);
  cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.setupSorting();
    this.loadSuperAdmins();
  }

  ngAfterViewInit() {
    // Initial sort will be applied after data loads
  }

  onSortChange(sort: Sort) {
    // Store the sort state when user clicks column headers
    this.currentSort = sort;
    this.sortData(sort);
  }

  private setupSorting() {
    this.tableDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name':
          return (item.fullName || '').toLowerCase();
        case 'email':
          return (item.email || '').toLowerCase();
        case 'phoneNumber':
          return (item.phoneNumber || '').toLowerCase();
        case 'lastLogin':
          if (!item.lastLoginAt) return 0;
          const lastLoginTime = this.parseDate(item.lastLoginAt).getTime();
          return isNaN(lastLoginTime) ? 0 : lastLoginTime;
        case 'createdDate':
          if (!item.createdAt) return 0;
          const createdTime = this.parseDate(item.createdAt).getTime();
          return isNaN(createdTime) ? 0 : createdTime;
        case 'status':
          return (item.status || '').toLowerCase();
        default:
          return (item as any)[property] || '';
      }
    };
  }

  private parseDate(dateString: string): Date {
    // Handle ISO 8601 format with extra decimal places
    const truncatedDate = dateString.replace(/(\.\d{3})\d+/, '$1');
    return new Date(truncatedDate);
  }

  private sortData(sort: Sort) {
    const data = this.tableDataSource.data.slice();
    
    if (!sort.active || sort.direction === '') {
      this.tableDataSource.data = data;
      return;
    }

    const sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const valueA = this.tableDataSource.sortingDataAccessor(a, sort.active);
      const valueB = this.tableDataSource.sortingDataAccessor(b, sort.active);
      
      return this.compare(valueA, valueB, isAsc);
    });

    this.tableDataSource.data = sortedData;
  }

  private compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private loadSuperAdmins() {
    this.isLoading = true;

    this.http.get<SuperAdminUser[]>(`${this.backendApi}/api/SuperAdmin/users`)
      .subscribe({
        next: (users) => {
          this.immutableTableData = users;
          this.filterBySegment(this.currentSegment);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load SuperAdmin users:', error);
          this.errorService.openandCloseError('Failed to load users');
          this.isLoading = false;
        }
      });
  }

  onTabChange(index: number) {
    const segments: Array<'active' | 'deactivated'> = ['active', 'deactivated'];
    this.currentSegment = segments[index];
    this.filterBySegment(this.currentSegment);
  }

  private filterBySegment(segment: 'active' | 'deactivated') {
    let filtered: SuperAdminUser[] = [];

    if (segment === 'active') {
      filtered = this.immutableTableData.filter(u => u.isApproved);
      this.pageTitle = 'Active SuperAdmin Users';
      this.pageSubtitle = 'Manage system super administrators';
    } else {
      filtered = this.immutableTableData.filter(u => !u.isApproved);
      this.pageTitle = 'Deactivated SuperAdmin Users';
      this.pageSubtitle = 'Previously deactivated administrators';
    }

    this.tableDataSource.data = filtered;
    
    // Apply current sort to the filtered data
    this.sortData(this.currentSort);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableDataSource.filter = filterValue.trim().toLowerCase();
  }

  viewDetails(user: SuperAdminUser) {
    const ref = this.dialog.open(SuperAdminDetailsComponent, {
      maxHeight: '93vh',
      maxWidth: '95vw',
      width: '800px',
      data: user
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.updated) {
        this.loadSuperAdmins();
      }
    });
  }

  deactivateUser(user: SuperAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: 'Deactivate SuperAdmin',
      message: `Are you sure you want to deactivate this SuperAdmin user? They will lose access to the system.`,
      actionType: 'archive',
      itemName: user.fullName,
      requiresReason: true,
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDeactivate(user, result.reason || 'No reason provided');
      }
    });
  }

  private performDeactivate(user: SuperAdminUser, reason: string) {
    this.http.post(`${this.backendApi}/api/SuperAdmin/users/${user.id}/deactivate`, { reason })
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'SuperAdmin user deactivated successfully' },
            duration: 3000,
          });
          this.loadSuperAdmins();
        },
        error: (err) => {
          console.error('Deactivate error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to deactivate user'
          );
        }
      });
  }

  reactivateUser(user: SuperAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: 'Reactivate SuperAdmin',
      message: `Are you sure you want to reactivate this SuperAdmin user? They will regain full system access.`,
      actionType: 'reactivate',
      itemName: user.fullName,
      requiresReason: false,
      confirmButtonText: 'Reactivate',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performReactivate(user);
      }
    });
  }

  private performReactivate(user: SuperAdminUser) {
    this.http.post(`${this.backendApi}/api/SuperAdmin/users/${user.id}/reactivate`, {})
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'SuperAdmin user reactivated successfully' },
            duration: 3000,
          });
          this.loadSuperAdmins();
        },
        error: (err) => {
          console.error('Reactivate error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to reactivate user'
          );
        }
      });
  }

  deleteUser(user: SuperAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: 'Delete SuperAdmin',
      message: `⚠️ WARNING: This action is PERMANENT and cannot be undone. Are you absolutely sure you want to delete this SuperAdmin user?`,
      actionType: 'delete',
      itemName: user.fullName,
      requiresReason: true,
      confirmButtonText: 'Delete Permanently',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDelete(user, result.reason || 'No reason provided');
      }
    });
  }

  private performDelete(user: SuperAdminUser, reason: string) {
    this.http.delete(`${this.backendApi}/api/SuperAdmin/users/${user.id}`, {
      body: { reason }
    })
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'SuperAdmin user deleted permanently' },
            duration: 3000,
          });
          this.loadSuperAdmins();
        },
        error: (err) => {
          console.error('Delete error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to delete user'
          );
        }
      });
  }

  resetPassword(user: SuperAdminUser) {
    const dialogData: ConfirmActionDialogData = {
      title: 'Reset Password',
      message: `Reset password for ${user.fullName}? A new temporary password will be sent to their email address (${user.email}).`,
      actionType: 'reset',
      itemName: user.fullName,
      requiresReason: false,
      confirmButtonText: 'Reset Password',
      cancelButtonText: 'Cancel'
    };

    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performPasswordReset(user);
      }
    });
  }

  private performPasswordReset(user: SuperAdminUser) {
    this.http.post(`${this.backendApi}/api/SuperAdmin/users/${user.id}/reset-password`, {})
      .subscribe({
        next: () => {
          this._sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Password reset successfully. New password sent via email.' },
            duration: 3000,
          });
          this.loadSuperAdmins();
        },
        error: (err) => {
          console.error('Password reset error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to reset password'
          );
        }
      });
  }

  navigateToCreate() {
    this.router.navigate(['/app/superadmin/create']);
  }
}